import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, X, CheckCircle2, Info } from "lucide-react";
import { PROJECTS, PROJECT_CATEGORIES, slugify } from "../data/site";
import { Reveal, EASE } from "./Reveal";
import { api, imgUrl } from "../lib/api";

export const DemoNote = () => (
  <div data-testid="gallery-demo-note" className="flex items-start gap-3 border border-steel bg-graphite px-5 py-4 text-xs sm:text-sm text-cement leading-relaxed">
    <Info size={16} className="text-forest-glow shrink-0 mt-0.5" />
    <span>
      Contenuti dimostrativi: immagini e schede di questa galleria sono esempi predisposti per essere sostituiti
      con fotografie e progetti reali dei cantieri Costruzioni Ciosko.
    </span>
  </div>
);

export const Gallery = ({ limit, showFilters = true }) => {
  const [cat, setCat] = useState("Tutti");
  const [active, setActive] = useState(null);
  const [projects, setProjects] = useState(PROJECTS);

  useEffect(() => {
    api("/projects").then((d) => { if (d.length) setProjects(d); }).catch(() => {});
  }, []);

  const all = cat === "Tutti" ? projects : projects.filter((p) => p.cat === cat);
  const items = limit ? all.slice(0, limit) : all;

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [active]);

  return (
    <div>
      {showFilters && (
        <Reveal className="flex flex-wrap gap-2 mb-10">
          {["Tutti", ...PROJECT_CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`filter-${slugify(c)}`}
              onClick={() => setCat(c)}
              className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] border transition-all duration-300 ${
                cat === c
                  ? "bg-forest border-forest-mid text-white"
                  : "border-steel text-cement hover:border-forest-glow hover:text-forest-glow"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>
      )}

      <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <AnimatePresence mode="popLayout">
          {items.map((p, i) => (
            <motion.article
              layout
              key={p.id}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.55, delay: i * 0.05, ease: EASE }}
            >
              <button
                data-testid={`project-card-${p.id}`}
                onClick={() => setActive(p)}
                className="group relative block w-full text-left overflow-hidden border border-steel bg-card focus:outline-none focus-visible:border-forest-glow"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={imgUrl(p.img)}
                    alt={`${p.title} — ${p.cat}, ${p.loc}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-base/90 via-base/20 to-transparent" aria-hidden="true" />
                  {p.demo !== false && (
                    <span className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.25em] bg-base/80 border border-steel text-cement px-2.5 py-1">Demo</span>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-forest-glow">{p.cat}</p>
                  <h3 className="font-display font-bold text-lg text-white mt-1.5 tracking-tight">{p.title}</h3>
                  <p className="flex items-center gap-1.5 text-xs text-cement mt-1.5"><MapPin size={12} /> {p.loc}</p>
                </div>
                <span className="absolute inset-x-0 bottom-0 h-[2px] bg-forest-glow scale-x-0 origin-left transition-transform duration-500 group-hover:scale-x-100" aria-hidden="true" />
              </button>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="project-modal"
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-base/90 backdrop-blur-sm" onClick={() => setActive(null)} aria-hidden="true" />
            <motion.div
              className="relative bg-card border border-steel max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              role="dialog"
              aria-modal="true"
              aria-label={active.title}
            >
              <button
                data-testid="project-modal-close"
                onClick={() => setActive(null)}
                aria-label="Chiudi dettaglio progetto"
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-base/80 border border-steel text-metal hover:border-forest-glow hover:text-forest-glow transition-colors"
              >
                <X size={18} />
              </button>
              <div className="aspect-[16/9] overflow-hidden">
                <img src={imgUrl(active.img)} alt={`${active.title} — ${active.cat}`} className="w-full h-full object-cover" />
              </div>
              <div className="p-6 sm:p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-forest-glow">{active.cat}{active.demo !== false && " · Contenuto dimostrativo"}</p>
                <h3 className="font-display font-bold text-2xl text-white mt-2 tracking-tight">{active.title}</h3>
                <p className="flex items-center gap-1.5 text-sm text-cement mt-2"><MapPin size={14} /> {active.loc}</p>
                <p className="text-metal/80 leading-relaxed mt-5">{active.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {active.details.map((d, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-cement">
                      <CheckCircle2 size={15} className="text-forest-glow shrink-0" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
